import React, { useState, useEffect } from 'react';
import * as Icon from 'react-feather';

const METRICS = [
  { key: 'impressions', label: 'Impressions',  icon: Icon.Eye,           color: '#4f46e5' },
  { key: 'reach',       label: 'Reach',        icon: Icon.Users,         color: '#0ea5e9' },
  { key: 'clicks',      label: 'Clicks',       icon: Icon.MousePointer,  color: '#f59e0b' },
  { key: 'likes',       label: 'Likes',        icon: Icon.Heart,         color: '#ef4444' },
  { key: 'comments',    label: 'Comments',     icon: Icon.MessageSquare, color: '#8b5cf6' },
  { key: 'shares',      label: 'Shares',       icon: Icon.Share2,        color: '#10b981' },
];

function timeAgo(dateStr) {
  if (!dateStr) return null;
  const diff = Math.round((Date.now() - new Date(dateStr).getTime()) / 60000); // minutes
  if (diff < 2)   return 'just now';
  if (diff < 60)  return `${diff}m ago`;
  const h = Math.round(diff / 60);
  if (h < 24)     return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

function PostStats({ postId }) {
  const [stats,   setStats]   = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!postId) return;
    fetch(`https://api.schedpilot.com/wp-json/api/v1/post-stats/${postId}`, {
      credentials: 'include',
    })
      .then(r => r.json())
      .then(data => {
        if (data.code === 200 && data.accounts?.length > 0) setStats(data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [postId]);

  if (loading) return null;

  const sectionLabel = {
    fontSize: '0.7rem', fontWeight: 600,
    letterSpacing: '0.08em', textTransform: 'uppercase', color: '#7d8fa3',
  };

  if (!stats) {
    return (
      <div style={{ border: '1px dashed #d1d9e0', borderRadius: 12, padding: '16px', marginTop: 12, textAlign: 'center' }}>
        <Icon.BarChart2 size={22} color="#d1d9e0" style={{ marginBottom: 6 }} />
        <p style={{ fontSize: '0.78rem', color: '#94a3b8', margin: 0 }}>
          Analytics will appear here after the first sync (runs every 6 hours).
        </p>
      </div>
    );
  }

  const lastSynced = stats.accounts.reduce((latest, a) => {
    if (!latest) return a.fetched_at;
    return new Date(a.fetched_at) > new Date(latest) ? a.fetched_at : latest;
  }, null);

  return (
    <div style={{ background: '#fff', border: '1px solid #d1d9e0', borderRadius: 12, padding: '16px', marginTop: 12 }}>

      {/* Header */}
      <div className="d-flex align-items-center justify-content-between mb-3">
        <span style={sectionLabel}>Post Analytics</span>
        {lastSynced && (
          <span style={{ fontSize: '0.68rem', color: '#94a3b8' }}>
            Synced {timeAgo(lastSynced)}
          </span>
        )}
      </div>

      {/* Metric tiles */}
      <div className="row g-2">
        {METRICS.map(({ key, label, icon: MetricIcon, color }) => (
          <div key={key} className="col-4">
            <div style={{ background: '#f8fafc', borderRadius: 8, padding: '10px 8px', textAlign: 'center' }}>
              <MetricIcon size={13} color={color} style={{ marginBottom: 3 }} />
              <div style={{ fontSize: '1.05rem', fontWeight: 700, color: '#0f172a', lineHeight: 1.2 }}>
                {(stats.totals[key] ?? 0).toLocaleString()}
              </div>
              <div style={{ fontSize: '0.62rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', marginTop: 1 }}>
                {label}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Per-account breakdown (only when multiple accounts) */}
      {stats.accounts.length > 1 && (
        <div style={{ marginTop: 12, borderTop: '1px solid #f1f5f9', paddingTop: 10 }}>
          {stats.accounts.map(acc => (
            <div key={acc.account_id} className="d-flex align-items-center justify-content-between"
              style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: 4 }}>
              <span style={{ textTransform: 'capitalize' }}>
                {acc.platform === 'linkedin_page' ? 'LinkedIn Page' : acc.platform}
              </span>
              <span style={{ color: '#334155', fontWeight: 600 }}>
                {(acc.impressions || 0).toLocaleString()} imp · {(acc.likes || 0).toLocaleString()} likes
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default PostStats;
